import axios from 'axios'
export type { AdvisorSupplierCard, AdvisorSupplierProfile } from '../types/advisor'
import type { AdvisorSupplierCard, AdvisorSupplierProfile } from '../types/advisor'
import { API_CONFIG } from '../config/api'

const api = axios.create({
    baseURL: API_CONFIG.baseURL,
    timeout: API_CONFIG.timeout
})

const FEATURED_SUPPLIERS = [
    'Authenticx', 'RingCentral', 'Zoom', 'Nextiva', 'Talkdesk',
    'Cato Networks', 'Aryaka', 'Five Star Call Centers', 'Netrix Global'
];

/**
 * Fetches the directory list of suppliers for advisors.
 */
export interface PeopleSearchResult {
    id: string
    name: string
    email: string | null
    jobTitle: string | null
    supplier: { id: string, name: string, logoUrl: string | null }
}

export async function fetchSupplierNames(token?: string): Promise<{ id: string, name: string }[]> {
    const res = await api.get('/api/advisors/me/suppliers/names', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
    })
    return res.data
}

export async function fetchAdvisorSuppliers(
    options: { search?: string, tsd?: string, page?: number, limit?: number | 'all', ids?: string[] } = {},
    token?: string
): Promise<{ suppliers: AdvisorSupplierCard[], total: number, people?: PeopleSearchResult[] }> {
    try {
        const params: any = {
            q: options.search,
            tsd: options.tsd,
            page: options.page,
            limit: options.limit,
            ids: options.ids?.join(',') || undefined
        };

        const res = await api.get('/api/advisors/me/suppliers', {
            params,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });

        // Backend now returns { data: [], meta: { total } }
        const { data, meta } = res.data;

        // Client-side visual override to ensure ~40% promo density (User Request)
        const suppliers = data.map((s: AdvisorSupplierCard) => {
            const isFeatured = FEATURED_SUPPLIERS.some(f => s.name.toLowerCase().includes(f.toLowerCase()));

            // If backend already sends standard 'hasActivePromo', we respect it OR apply our own filter if it's too aggressive
            // Given the 100% issue, we will force the randomness here.

            const originalHasPromo = s.hasActivePromo; // Or whatever backend sends

            let finalHasPromo = originalHasPromo;

            if (finalHasPromo) {
                // If backend says yes, apply filter:
                // Only keep it if it's Featured OR 40% random chance
                if (!isFeatured && Math.random() > 0.4) {
                    finalHasPromo = false;
                }
            }

            return {
                ...s,
                hasActivePromo: finalHasPromo
            };
        });

        return {
            suppliers,
            total: meta?.total || suppliers.length,
            people: res.data.people || undefined
        };

    } catch (error) {
        console.error('Failed to fetch advisor suppliers', error);
        throw error;
    }
}

/**
 * Fetches the detailed profile of a supplier for advisors.
 */
export async function fetchAdvisorSupplierProfile(supplierId: string, token?: string): Promise<AdvisorSupplierProfile> {
    try {
        const res = await api.get(`/api/suppliers/${supplierId}/overview`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        return res.data;
    } catch (error) {
        console.error('Failed to fetch advisor supplier profile', error);
        throw error;
    }
}

/**
 * Fetches comparison data for multiple suppliers (2-3).
 */
export async function fetchSupplierComparison(supplierIds: string[], token?: string): Promise<{ suppliers: AdvisorSupplierProfile[] }> {
    try {
        const res = await api.get('/api/suppliers/compare', {
            params: { ids: supplierIds.join(',') },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        return res.data;
    } catch (error) {
        console.error('Failed to fetch supplier comparison', error);
        throw error;
    }
}

// ── Advisor → supplier ratings ──
export interface SupplierRating {
    id: string
    supplier_id: string
    advisor_id: string
    quote_response_time: number | null
    implementation_quality: number | null
    support_quality: number | null
    accuracy_vs_marketing: number | null
    overall: number | null
    deal_size_range: string | null
    solution_category: string | null
    review_text: string | null
    created_at: string
}

export interface SupplierRatingInput {
    overall: number
    quote_response_time?: number
    implementation_quality?: number
    support_quality?: number
    accuracy_vs_marketing?: number
    deal_size_range?: string
    solution_category?: string
    review_text?: string
}

export async function getSupplierRatings(supplierId: string, token: string): Promise<{ ratings: SupplierRating[]; mine: SupplierRating | null }> {
    const res = await api.get(`/api/advisors/workspace/suppliers/${supplierId}/ratings`, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}

export async function submitSupplierRating(supplierId: string, input: SupplierRatingInput, token: string): Promise<SupplierRating> {
    const res = await api.post(`/api/advisors/workspace/suppliers/${supplierId}/ratings`, input, { headers: { Authorization: `Bearer ${token}` } })
    return res.data
}
